/**
 * Duplicate Lot Component (Wrapper)
 *
 * This is a minimal wrapper around the generic DuplicateEntity component
 * that handles lot-specific mutation logic.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client/react'
import { enqueueSnackbar } from 'notistack'
import DuplicateEntityComponent from './index.jsx'
import { LOT_CONFIG } from './entityConfigs'
import { COPY_LOT } from '@/housefolios-components/Lot/DuplicateLot/mutations'
import { GET_LOTS } from '@/housefolios-components/Lots/queries'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'

/**
 * Duplicate Lot Component
 *
 * @param {Object} props - Component props
 * @param {string} props.lotId - ID of the lot to duplicate
 * @param {Object} props.lot - The lot object to duplicate
 * @param {Array} props.portfolios - Available portfolios
 * @param {string} props.selectedPortfolio - Currently selected portfolio
 * @param {Function} props.setSelected - Function to clear selection after duplication
 * @param {Object} props.session - User session object
 * @param {Function} props.onSuccess - Success callback
 * @param {Function} props.onError - Error callback
 * @param {boolean} props.notifications - Whether to show as notification menu item
 * @param {boolean} props.rightClickMenu - Whether to show as right-click menu item
 * @param {string} props.buttonText - Custom button text
 */
function DuplicateLot(props) {
  const {
    lotId,
    lot,
    portfolios,
    selectedPortfolio: initialSelectedPortfolio,
    setSelected,
    session,
    onSuccess,
    onError,
    notifications = false,
    rightClickMenu = false,
    buttonText = '',
    ...otherProps
  } = props

  // Local state for portfolio selection
  const [selectedPortfolio, setSelectedPortfolio] = useState(
    initialSelectedPortfolio,
  )

  // GraphQL mutation
  const [copyLot] = useMutation(COPY_LOT)

  // Handle form submission
  const handleSubmit = async (additionalState = {}) => {
    if (!selectedPortfolio) {
      enqueueSnackbar('Please select a portfolio', {
        variant: 'warning',
      })
      return
    }

    try {
      const result = await copyLot({
        awaitRefetchQueries: true,
        variables: {
          lotId: lotId,
          portfolioId: selectedPortfolio,
          lotInput: additionalState, // Pass additional fields
        },
        refetchQueries: [
          {
            query: GET_LOTS,
            variables: { portfolioId: selectedPortfolio },
          },
          { query: GET_PORTFOLIOS },
          { query: GET_ME },
        ],
      })

      // Success handling
      enqueueSnackbar('Lot was duplicated successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      })

      if (setSelected) setSelected([])
      if (onSuccess) onSuccess(result)
    } catch (error) {
      // Error handling
      console.error('Failed to duplicate lot:', error)
      enqueueSnackbar('Lot failed to duplicate', {
        variant: 'error',
        autoHideDuration: 3000,
      })

      if (onError) onError(error)
    }
  }

  return (
    <DuplicateEntityComponent
      {...otherProps}
      entityType="lot"
      entityId={lotId}
      entity={lot}
      portfolios={portfolios}
      selectedPortfolio={selectedPortfolio}
      onPortfolioChange={setSelectedPortfolio}
      setSelected={setSelected}
      session={session}
      config={LOT_CONFIG}
      onSuccess={onSuccess}
      onError={onError}
      onSubmit={handleSubmit}
      notifications={notifications}
      rightClickMenu={rightClickMenu}
      sidebarMenu={false} // Lots don't typically use sidebar menu
      underwriting={false} // Lots don't have underwriting context
      overLimit={false} // Lots don't have limit restrictions
      toggleLimit={() => {}} // No-op function for limit toggle
      buttonText={buttonText}
    />
  )
}

// PropTypes - including rendering props
DuplicateLot.propTypes = {
  lotId: PropTypes.string.isRequired,
  lot: PropTypes.object,
  portfolios: PropTypes.array.isRequired,
  selectedPortfolio: PropTypes.string,
  setSelected: PropTypes.func,
  session: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  notifications: PropTypes.bool,
  rightClickMenu: PropTypes.bool,
  buttonText: PropTypes.string,
}

export default DuplicateLot
